import { useMsal } from '@azure/msal-react';
import { Header } from '../header/header';
import './profile-page.css';
import { Button, Card, Carousel, Empty, Form, Progress, Row, Select, Tooltip, Spin } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import RankingCard from '../reporting/ranking-card';
import { strings } from '../../lang';
import { Achievement, ActionDto, PowerHourConfig, RatingDto, UserDataDto } from '../../dto/reporting-models';
import { GetAccessToken } from '../../utils/auth-utils';
import { scopes } from '../../authConfig';
import { useEffect, useState } from 'react';
import { reportingService } from '../../services/reporting-service';
import dayjs from 'dayjs';
import PowerHourRace from '../power-hour/power-hour-race';

export const ProfilePage = () => {
  const { instance, inProgress } = useMsal();
  const [form] = Form.useForm();
  const [userProfile, setUserProfile] = useState<UserDataDto | undefined>(undefined);
  const [expRating, setExpRating] = useState<RatingDto | null>(null);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [achievementsRarity, setAchievementsRarity] = useState<Record<number, number>>([]);
  const [actions, setActions] = useState<ActionDto[] | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [customizationChanged, setCustomizationChanged] = useState<boolean>(false);
  const [selectedButton, setSelectedButton] = useState<string | undefined>(userProfile?.selectedBookingButton ? userProfile?.selectedBookingButton : 'default');
  const [isLoading, setIsLoading] = useState(true);
  const [powerHourConfig, setPowerHourConfig] = useState<PowerHourConfig | null>(null);

  const [powerHourRefreshIsLoading, setPowerHourRefreshIsLoading] = useState(false);
  const [powerHourRefreshKey] = useState(0);
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const accessToken = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const expOrder = await reportingService.getTopByExperienceAsync(accessToken.accessToken);
        const rarity = await reportingService.getAchievementsRarity(accessToken.accessToken);
        const achievementsResponse = await reportingService.getAchievementsAsync(accessToken.accessToken);
        setExpRating(expOrder);
        setAchievementsRarity(rarity);
        setAchievements(achievementsResponse);

        const response = await reportingService.getUserDataAsync(false, accessToken.accessToken);
        setUserProfile(response);
        setSelectedButton(response.selectedBookingButton);
      } catch (error) {
        setError('Error fetching user data');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [instance, inProgress]);

  useEffect(() => {
    const fetchActions = async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      try {
        const actionsResponse = await reportingService.getActionsAsync(token.accessToken);
        setActions(actionsResponse);
      } catch (error) {}
    };
    const fetchActivePowerHour = async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      try {
        let activePowerHour: PowerHourConfig | null = null;
        activePowerHour = await reportingService.getActivePowerHourConfig(token.accessToken);
        if (!activePowerHour) {
          activePowerHour = await reportingService.getLastActivePowerHourConfig(token.accessToken);
        }
        setPowerHourConfig(activePowerHour);
      } catch (error) {}
    };
    fetchActions();
    fetchActivePowerHour();
  }, []);

  const calculateReceivedXpPercentage = () => {
    return +((userProfile?.currentXp! / userProfile?.nextLevelXp!) * 100).toFixed(1);
  };

  const days = [
    { id: 0, name: 'Sunday' },
    { id: 1, name: 'Monday' },
    { id: 2, name: 'Tuesday' },
    { id: 3, name: 'Wednesday' },
    { id: 4, name: 'Thursday' },
    { id: 5, name: 'Friday' },
    { id: 6, name: 'Saturday' },
  ];

  const setAchievementRarity = (achievement: Achievement) => {
    const rarity = (achievementsRarity[achievement.id] * 100).toFixed(0);
    if (+rarity <= 10) {
      return <div>Rarity: Ultra rare ({rarity}%)</div>;
    } else if (+rarity <= 30) {
      return <div>Rarity: Rare ({rarity}%)</div>;
    } else {
      return <div>Rarity: Common ({rarity}%)</div>;
    }
  };

  const getAchievementTooltip = (achievement: Achievement) => {
    return (
      <>
        {achievement && achievement?.description && <div>{achievement.description}</div>}
        {achievement?.fromHour && (
          <div>
            Time frame: {achievement.fromHour} - {achievement.toHour}
          </div>
        )}
        {achievement?.days && <div>Days: {achievement?.days?.map((d) => `${days.find((day) => day.id === +d)?.name} `)}</div>}
        {achievement?.dueDate && <div>Due date: {dayjs(achievement?.dueDate!).format('DD.MM.YYYY')}</div>}
        {setAchievementRarity(achievement)}
      </>
    );
  };

  const getButtonOptions = () => {
    let options = [{ label: 'Default', value: 'default', className: 'btn-grad-prime' }];

    if (userProfile?.currentLevel && userProfile?.currentLevel >= 4) {
      options.push({ label: 'Bronze Button', value: 'BronzeButton', className: 'btn-grad-bronze' });
    }
    if (userProfile?.currentLevel && userProfile?.currentLevel >= 6) {
      options.push({ label: 'Silver Button', value: 'SilverButton', className: 'btn-grad-silver' });
    }
    if (userProfile?.currentLevel && userProfile?.currentLevel >= 8) {
      options.push({ label: 'Golden Button', value: 'GoldButton', className: 'btn-grad-gold' });
    }
    if (userProfile?.currentLevel && userProfile?.currentLevel >= 11) {
      options.push({ label: 'Space Button', value: 'SpaceButton', className: 'btn-grad-space' });
    }

    return options;
  };

  const saveCustomization = async () => {
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    await reportingService.ModifyCustomizableUIAsync(token.accessToken, { selectedBookingButton: selectedButton });
  };

  const getContent = () => {
    if (error) {
      return (
        <>
          <div className="center-content">
            <Empty description="">
              <h3>{strings.missingUserProfile}</h3>
            </Empty>
          </div>
        </>
      );
    }

    return (
      <div className="profile-page">
        <h2 className="profile-title">{userProfile?.fullName}</h2>
        <div className="profile-info">
          <Row gutter={8}>
            <div style={{ marginRight: 20, width: 800 }}>
              <Spin spinning={isLoading}>
                <Card className="activity-card" style={{ maxHeight: 'fit-content', margin: '0 0 20px 0' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{ margin: '0 0 20px 0' }}>
                      Level {userProfile?.currentLevel} {userProfile?.levelName}
                    </h3>

                    <div className="progress-part">
                      <Tooltip title={`${strings.profileRemainingXpTillNextLvl} ${userProfile?.nextLevelXp! - userProfile?.currentXp!}`}>
                        <Progress
                          rootClassName="profile-xp-progress"
                          strokeColor="var(--text-primary)"
                          percent={calculateReceivedXpPercentage()}
                          percentPosition={{ align: 'center', type: 'inner' }}
                        />
                      </Tooltip>
                      <Tooltip
                        overlayStyle={{ whiteSpace: 'pre-line' }}
                        title={actions
                          ?.filter((a) => a.points > 0)
                          .map((action) => (
                            <div>
                              {strings.getActionName(
                                action.actionType as 'Booked' | 'NotReached' | 'ReachedNotBooked' | 'CustomerLost' | 'RevisionQueueAssignment' | 'WrongPhoneNumber' | 'AppointmentHappened'
                              )}{' '}
                              - {action.points}
                            </div>
                          ))}
                      >
                        <QuestionCircleOutlined style={{ marginLeft: 10, fontSize: 34 }} />
                      </Tooltip>
                    </div>
                  </div>
                </Card>
              </Spin>

              <Spin spinning={isLoading}>
                <Card className="activity-card">
                  <h3>Achievements</h3>
                  <Carousel arrows infinite={false} slidesToShow={4} dots={true} effect="scrollx">
                    {userProfile?.achievements?.map((achievement) => (
                      <Tooltip key={achievement.id} title={getAchievementTooltip(achievements.find((ach) => ach.id === achievement.id)!)}>
                        <div style={{ opacity: achievement.isCompleted ? 1 : 0.5, textAlign: 'center' }}>
                          {achievements.find((ach) => ach.id === achievement.id)?.icon && (
                            <img
                              src={achievements.find((ach) => ach.id === achievement.id)?.icon === null ? '' : decodeURIComponent(achievements.find((ach) => ach.id === achievement.id)?.icon!)}
                              alt="achievement"
                              style={{ display: 'block', margin: '0 auto', width: '100px', height: '100px' }}
                            />
                          )}
                          <h4>
                            {achievement.isCompleted
                              ? achievement.name
                              : `${achievement.name} (${((achievement.progress / achievements.find((ach) => ach.id === achievement.id)?.target!) * 100).toFixed(0)}%)`}
                          </h4>
                        </div>
                      </Tooltip>
                    ))}
                  </Carousel>
                </Card>
              </Spin>

              {powerHourConfig && (
                <Spin spinning={powerHourRefreshIsLoading}>
                  <Card
                    className="activity-card"
                    style={{
                      maxWidth: 800,
                      borderRadius: 8,
                      boxShadow: '0 2px 8px var(--shadow-medium)',
                    }}
                    bodyStyle={{ padding: 24 }}
                  >
                    <h3>{strings.powerHour.title}</h3>
                    <PowerHourRace powerHourConfig={powerHourConfig} refreshKey={powerHourRefreshKey} setIsLoading={setPowerHourRefreshIsLoading} />
                  </Card>
                </Spin>
              )}

              {userProfile?.currentLevel && userProfile?.currentLevel >= 4 && (
                <Spin spinning={isLoading}>
                  <Card className="activity-card">
                    <h3>Customization</h3>
                    <Form
                      wrapperCol={{ span: 7 }}
                      form={form}
                      onFinish={async () => {
                        await saveCustomization();
                        setCustomizationChanged(false);
                      }}
                    >
                      <Form.Item label="Calendar Button">
                        <Select
                          value={getButtonOptions().find((button) => button.value === (selectedButton ?? 'default'))?.value}
                          onChange={(value) => {
                            setCustomizationChanged(true);
                            setSelectedButton(value);
                          }}
                          disabled={isLoading}
                        >
                          {getButtonOptions().map((button) => (
                            <Select.Option style={{ marginTop: '5px' }} key={button.value} value={button.value} className={button.className}>
                              {button.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      {customizationChanged && (
                        <div className="bg-gray-50 p-4 rounded-md" style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                          <Button
                            onClick={() => {
                              setCustomizationChanged(false);
                              setSelectedButton(userProfile?.selectedBookingButton);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button type="primary" htmlType="submit">
                            Save
                          </Button>
                        </div>
                      )}
                    </Form>
                  </Card>
                </Spin>
              )}
            </div>
          </Row>
          <Row style={{ height: 'fit-content' }}>
            <Spin spinning={isLoading}>
              <RankingCard title={strings.profileTopExperience} rankData={expRating ?? null} />
            </Spin>
          </Row>
        </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      {getContent()}
    </>
  );
};
